import React, { useContext, useMemo } from "react";
import { Card } from "react-bootstrap";
import MaterialTable from 'material-table';
import icons from 'MaterialTableIcons';
import { Link } from 'react-router-dom';
import { EngineIcon, engineLabels, truncateCenter } from 'Utils'
import AppContext from "Utils/AppContext";
import DiscoveryTooltip from "DiscoveryTooltip";
import AdoptedFromBadge from "DataSources/AdoptedFromBadge";

const SearchColumn = (props) => (
  <div
    className="pl-3 pt-3 pb-3"
  >
    <DiscoveryTooltip text='View Discovery results'>
      <Link to={`/results/${props.searchid}`}>
        {truncateCenter((props.displayId || props.searchid), 48, '..')}
      </Link>
    </DiscoveryTooltip>
    <AdoptedFromBadge
      registeredServiceId={props.registeredServiceId}
      registeredServiceName={props.registeredServiceName}
    />
  </div>
);

const getMonth = (date) => {
  const dateOptions = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric'
  };
  const result = (new Date(date)).toLocaleDateString('en-US', dateOptions);
  return result;
}

const getTime = (date) => {
  const timeOptions = { hour: 'numeric', minute: 'numeric', hour12: true };
  const result = (new Date(date)).toLocaleString('en-US', timeOptions);
  return result;
}

const ReportsTable = (props) => {
  const tableRef = React.createRef();
  const { rulesMap, connections } = useContext(AppContext);
  // Lookup table: dataSourceId → { registeredServiceId } so each run row can show an
  // "imported from <integration>" badge by joining the run DTO against the data source list
  // already loaded into AppContext on app start. The run DTO itself doesn't carry origin —
  // see umbrella TODO for backend enrichment that would remove this client-side join.
  const dataSourceById = useMemo(() => {
    const m = new Map();
    (connections || []).forEach((c) => {
      if (c && c.id != null) m.set(String(c.id), c);
    });
    return m;
  }, [connections]);

  const COLUMNS = [
    {
      title: 'DATA SOURCE',
      field: 'connection',
      headerStyle: {
        fontFamily: 'Rubik'
      },
      cellStyle: {
        fontFamily: 'Rubik',
      },
      render: ({ connection, dataSourceId }) => {
        const ds = dataSourceId != null ? dataSourceById.get(String(dataSourceId)) : null;
        const registeredServiceId = ds?.registeredServiceId || null;
        return dataSourceId != null ? (
          <span>
            <DiscoveryTooltip text='View Risk Summary' placement='top'>
              <Link to={`/landscape/riskprofile/${encodeURIComponent(dataSourceId)}`}>
                {connection || `#${dataSourceId}`}
              </Link>
            </DiscoveryTooltip>
            <AdoptedFromBadge
              registeredServiceId={registeredServiceId}
              registeredServiceName={ds?.registeredServiceName}
            />
          </span>
        ) : (
          <span>{connection || '—'}</span>
        );
      }
    },
    {
      title: 'COMPLETED',
      field: 'date',
      type: 'string', // Force MaterialTable to treat as string and always use render function
      width: "180px",
      headerStyle: {
        fontFamily: 'Rubik',
        textAlign: 'center'
      },
      cellStyle: {
        fontFamily: 'Rubik',
        textAlign: 'center',
      },
      render: (rowData) => {
        // Always format dates using the render function for both parent and child rows
        if (!rowData || !rowData.date) {
          return <span>-</span>;
        }
        // Ensure date is a valid number/timestamp
        const dateValue = typeof rowData.date === 'number' ? rowData.date : new Date(rowData.date).getTime();
        if (isNaN(dateValue)) {
          return <span>-</span>;
        }
        const month = getMonth(dateValue);
        const time = getTime(dateValue);

        return (
          <span>{month} @ {time}</span>
        )
      }
    },
    {
      title: 'RESULTS',
      field: 'searchid',
      // width: '360px',
      headerStyle: {
        fontFamily: 'Rubik'
      },
      cellStyle: {
        fontFamily: 'Rubik',
      },
      render: (rowData) => {
        const ds = rowData.dataSourceId != null ? dataSourceById.get(String(rowData.dataSourceId)) : null;
        const registeredServiceId = ds?.registeredServiceId || null;
        // Connection parent rows show the most recent search result's discovery ID with link
        if (rowData.isConnectionParent) {
          return (
            <SearchColumn
              searchid={rowData.mostRecentSearchId}
              displayId={rowData.displayId}
              registeredServiceId={registeredServiceId}
              registeredServiceName={ds?.registeredServiceName}
            />
          );
        }
        return (
          <SearchColumn
            searchid={rowData.searchid}
            displayId={rowData.displayId}
            registeredServiceId={registeredServiceId}
            registeredServiceName={ds?.registeredServiceName}
          />
        )
      }
    },
    {
      title: 'SOURCE TYPE',
      field: 'type',
      width: '175px',
      headerStyle: {
        fontFamily: 'Rubik'
      },
      cellStyle: {
        fontFamily: 'Rubik'
      },
      render: (rowData) => {
        const type = rowData.type;
        if (!type) {
          return <span>-</span>;
        }
        return (
          <span>
            <EngineIcon engine={type} classes='mr-2' size='1.1rem' />
            {engineLabels[type]}
          </span>
        );
      }
    },

    {
      title: 'Sampling Rules',
      field: 'sampleRules',
      width: 225,
      render: (rowData) => {
        const rulesExist = rowData.sampleRules in rulesMap;

        if (rulesExist) {
          return (
            <DiscoveryTooltip text='View sampling rules details' placement='top'>
              <Link to={`/rules/rules-editor/${encodeURIComponent(rowData.sampleRules)}`}>
                { rowData.sampleRules }
              </Link>
            </DiscoveryTooltip>
          );
        }

        return (
          <div>{ rowData.sampleRules || '-' }</div>
        )
      }
    }
  ];

  // Filter out any objects that have a parent_searchid value
  const filteredResultSets = props.resultSets.filter(item => !item.parent_searchid);

  // console.log('props.resultSets', props.resultSets)
  return (
    <Card className="reports-table">
      <MaterialTable
        isLoading={props.fetchingData}
        tableRef={tableRef}
        icons={icons}
        padding='dense'
        columns={COLUMNS}
        data={filteredResultSets}
        options={{
          showTitle: false,
          pageSize: props.pageSize,
          tableLayout: 'fixed',
          search: false,
          toolbar: false
        }}
        parentChildData={(row, rows) => {
          // Match children to connection parents using parent_connection field
          if (row.parent_connection) {
            return rows.find(a => a.searchid === row.parent_connection);
          }
          return null;
        }}
      />
    </Card>
  );
}

export default ReportsTable;
